import { useEffect, useState } from 'react';
import { useApolloClient, useFragment } from '@apollo/client';
import { t, ngettext, msgid } from 'ttag';
import cookie from 'js-cookie';

import {
  ProductMultipleActionStatus,
  ProductStatus,
  ProductUserStatus,
  ProductVisibleStatus,
  PromoActionTypeEnum,
  SellingConditionEnum,
  TagsEnum,
  UserBlockedBy,
} from '@models';
import { SWAP_PARTY_COOKIE, UNKNOWN_ERROR_MESSAGE } from '@constants';
import { useFreePushups } from '@hook/useFreePushups';
import { getFieldsSingleError } from '@utils/common';
import { notifyError, notifySuccess } from '@utils/notifications';
import {
  sendAddPromoActionEvent,
  sendApplyPromoActionEvent,
  sendDeletePromoActionEvent,
  sendSwapItemAddedEvent,
} from '@utils/analytics';
import { getIsProductCanBePromoted } from '@utils/products';
import {
  ProductDetailsSellerActions,
  ProductDetailsSellerActionsSkeleton,
} from '@components/product/ProductDetailsSellerActions';
import { PromoActionFormModalContainer } from '@containers/PromoActionFormModal';
import { ModalInfoData, RemoveProductFromSwapInfoModal } from '@components/RemoveProductFromSwapInfoModal';

import {
  ProductDetailsSellerActionsFragment,
  ProductDetailsSellerActionsFragmentDoc,
  useProductDetailsActivateProductMutation,
  useProductDetailsDeactivateProductMutation,
  useProductDetailsUpdateProductStatusMutation,
} from './gql/graphql.v3.generated';
import {
  useAddProductToSwapMutation,
  useProductDetailsRemoveFromSwapInfoQuery,
  useRemoveProductFromSwapMutation,
} from './gql/graphql.v5.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsSellerActionsContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const client = useApolloClient();

  const [isPromoActionFormModalOpen, setIsPromoActionFormModalOpen] = useState(false);
  const [isSwapEnabled, setIsSwapEnabled] = useState(false);
  const [swapRemoveModalInfoData, setSwapRemoveModalInfoData] = useState<ModalInfoData | null>(null);

  const {
    freePushupsLeft,
    freePushupAvailable,
    canUseFreePush,
    handleUseFreePushupOnProduct,
  } = useFreePushups(product?.id);

  const { data, complete } = useFragment<ProductDetailsSellerActionsFragment>({
    fragment: ProductDetailsSellerActionsFragmentDoc,
    from: product || null,
  });

  const {
    data: swapInfoData,
  } = useProductDetailsRemoveFromSwapInfoQuery({
    variables: {
      productId: product?.id as number,
    },
    context: {
      v5: true,
    },
    skip: !product?.id && data?.sellingCondition !== SellingConditionEnum.Exchange,
  });

  const [updateProductStatus] = useProductDetailsUpdateProductStatusMutation({
    update: (cache, result) => {
      if (!result.errors?.length) {
        const normalizedProductId = cache.identify({
          id: product?.id,
          __typename: 'ProductDetails'
        });

        cache.modify({
          id: normalizedProductId,
          fields: {
            statusTitle: (value: ProductVisibleStatus) => {
              return value === ProductVisibleStatus.Reserved
                ? ProductVisibleStatus.Available
                : ProductVisibleStatus.Reserved;
            },
          },
        });
      }
    },
  });

  const [activateProduct] = useProductDetailsActivateProductMutation({
    variables: {
      includeIds: [product?.id as number],
    },
    update: (cache, result) => {
      if (!result.errors?.length) {
        const normalizedProductId = cache.identify({
          id: product?.id,
          __typename: 'ProductDetails'
        });

        cache.modify({
          id: normalizedProductId,
          fields: {
            userStatusTitle: () => {
              return ProductUserStatus.Default;
            },
            isProcessingMultipleAction: () => {
              return ProductMultipleActionStatus.Activating;
            },
          },
        });
      }
    },
  });

  const [deactivateProduct] = useProductDetailsDeactivateProductMutation({
    variables: {
      includeIds: [product?.id as number],
    },
    update: (cache, result) => {
      if (!result.errors?.length) {
        const normalizedProductId = cache.identify({
          id: product?.id,
          __typename: 'ProductDetails',
        });

        cache.modify({
          id: normalizedProductId,
          fields: {
            userStatusTitle: () => {
              return ProductUserStatus.Hidden;
            },
            isProcessingMultipleAction: () => {
              return ProductMultipleActionStatus.Deactivating;
            },
          },
        });
      }
    },
  });

  const [addProductToSwap] = useAddProductToSwapMutation({
    context: {
      v5: true,
    },
    update: (cache, result) => {
      if (!result.errors?.length) {
        const normalizedProductId = cache.identify({
          id: product?.id,
          __typename: 'ProductDetails',
        });

        cache.modify({
          id: normalizedProductId,
          fields: {
            isAvailableExchange: () => {
              return false;
            },
          },
        });
      }
    },
  });

  const [removeProductFromSwap] = useRemoveProductFromSwapMutation({
    context: {
      v5: true,
    },
    update: (cache, result) => {
      if (!result.errors?.length) {
        const normalizedProductId = cache.identify({ __typename: 'RemoveProductFromSwapInfoType' });

        cache.modify({
          id: normalizedProductId,
          fields: {
            isRemoveAvailable: () => {
              return false;
            },
          },
        });
      }
    },
  });

  const handleReserve = async () => {
    try {
      const response = await updateProductStatus({
        variables: {
          productId: product?.id as number,
          status: ProductStatus.Reserved,
        },
      });

      if (response.data?.updateProductStatus?.isSuccess) {
        notifySuccess(t`Товар поставлений на резерв`);
      } else {
        notifyError(t`Сталася помилка, не вдалося товар поставити на резерв.`);
      }
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };

  const handleUnReserve = async () => {
    try {
      const response = await updateProductStatus({
        variables: {
          productId: product?.id as number,
          status: ProductStatus.Visible,
        },
      });

      if (response.data?.updateProductStatus?.isSuccess) {
        notifySuccess(t`Товар знятий з резерву.`);
      } else {
        notifyError(t`Сталася помилка, не вдалося товар зняти з резерву.`);
      }
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };
  
  const handleActivate = async () => {
    try {
      const response = await activateProduct();

      if (response.data?.activateProducts?.errors?.length) {
        const errorsMapping = getFieldsSingleError(response.data.activateProducts.errors);

        const errorMessage = Object.values(errorsMapping)[0];
        notifyError(errorMessage);
      } else {
        notifySuccess(t`Через декілька хвилин товар буде активовано.`);
      }
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };
  
  const handleDeactivate = async () => {
    try {
      const response = await deactivateProduct();

      if (response.data?.deactivateProducts?.errors?.length) {
        const errorsMapping = getFieldsSingleError(response.data.deactivateProducts.errors);

        const errorMessage = Object.values(errorsMapping)[0];
        notifyError(errorMessage);
      } else {
        notifySuccess(t`Через декілька хвилин товар буде деактивовано.`);
      }
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };
  
  const handleFreePushup = async () => {
    try {
      const response = await handleUseFreePushupOnProduct({
        variables: {
          productId: product?.id as number,
        },
      });

      if (response.data?.useFreePushupOnProduct.errors?.length) {
        const errorsMapping = getFieldsSingleError(response.data.useFreePushupOnProduct.errors);

        const errorMessage = Object.values(errorsMapping)[0];
        notifyError(errorMessage);
      } else {
        const freePushupsLeftUpdated = response.data?.useFreePushupOnProduct.freePushupsLeft || 0;

        const freePushupsLeftText = ngettext(
          msgid`${freePushupsLeftUpdated} підняття`,
          `${freePushupsLeftUpdated} підняття`,
          `${freePushupsLeftUpdated} підняттів`,
          freePushupsLeftUpdated
        );

        notifySuccess(t`Вибраний товар було піднято. Доступно ще ${freePushupsLeftText}`);
      }
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };

  const handleAddOrEditPromoAction = () => {
    setIsPromoActionFormModalOpen(true);
    sendAddPromoActionEvent('product_details');
  };

  const handleAddOrEditPromoActionSuccess = async () => {
    const normalizedProductId = client.cache.identify({
      id: product?.id,
      __typename: 'ProductDetails',
    });

    client.cache.modify({
      id: normalizedProductId,
      fields: {
        tags: (value: TagsEnum[] | null) => {
          return value?.length ? [...value, TagsEnum.FreeShipping] : [TagsEnum.FreeShipping];
        },
      },
    });

    setIsPromoActionFormModalOpen(false);
    sendApplyPromoActionEvent('product_details');
  };

  const handleDeletePromoActionSuccess = async () => {
    const normalizedProductId = client.cache.identify({
      id: product?.id,
      __typename: 'ProductDetails',
    });

    client.cache.modify({
      id: normalizedProductId,
      fields: {
        tags: (value: TagsEnum[] | null) => {
          return value?.length ? value.filter((tag) => tag !== TagsEnum.FreeShipping) : [];
        },
      },
    });

    setIsPromoActionFormModalOpen(false);
    sendDeletePromoActionEvent();
  };

  const handleAddToSwap = async () => {
    try {
      const { data } = await addProductToSwap({
        variables: {
          productId: Number(product?.id),
        },
      });

      if (data?.addProductToSwap.errors?.length) {
        const errors = getFieldsSingleError(data?.addProductToSwap.errors);
        const errorMessage = Object.values(errors)[0];

        notifyError(errorMessage);
        return;
      }

      sendSwapItemAddedEvent();

      notifySuccess(t`Товар відправлено на модерацію. Після перевірки його буде додано до SWAP-паті`);
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };

  const handleRemoveProductFromSwap = async () => {
    try {
      setSwapRemoveModalInfoData(null);

      const { data } = await removeProductFromSwap({
        variables: {
          productId: Number(product?.id),
        },
      });

      if (data?.removeProductFromSwap.errors?.length) {
        const errors = getFieldsSingleError(data?.removeProductFromSwap.errors);
        const errorMessage = Object.values(errors)[0];

        notifyError(errorMessage);
        return;
      }

      notifySuccess(t`Товар видалено зі SWAP-паті`);

      window.location.reload();
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  };

  const handleRemoveFromSwapClick = () => {
    const popupInfo = swapInfoData?.removeFromSwapInfoForProduct.popupInfo;

    if (popupInfo) {
      setSwapRemoveModalInfoData({
        title: popupInfo.title,
        description: popupInfo.text,
        removeButtonText: popupInfo.removeText,
      });
    }
  };

  const isPromoActionAdded = !!data?.promoActions?.find((promo) => promo.isAdded);

  const isProcessingMultipleAction = data?.isProcessingMultipleAction !== ProductMultipleActionStatus.Default;
  const isProductHidden = data?.userStatusTitle === ProductUserStatus.Hidden;
  const isProductAvailable = data?.statusTitle === ProductVisibleStatus.Available;
  const isProductReserved = data?.statusTitle === ProductVisibleStatus.Reserved;
  // def can_be_promoted(self) -> bool:
  //   """Check whether product can be promoted"""
  //   return (
  //       self.status == Product.STATUS_VERIFIED
  //       and self.visibility == Product.VISIBILITY_VISIBLE
  //       and self.catalog_id not in settings.PROMOTION_DISALLOWED_CATALOGS ???????
  //       and self.user_status != Product.USER_STATUS_HIDDEN
  //       and not self.is_processing_multiple_action()
  //       and s_flags.PROMOTION_ENABLE
  const isProductCanBePromoted = getIsProductCanBePromoted(data);
  const isProductCanBeReserved = (
    isProductAvailable
    && Number(data?.availabilityCount) <= 1
  );
  const isProductCanBeUnReserved = (
    isProductReserved
    && !data?.isOutOfStock
  );
  const isCanBeFreePushups = !!freePushupsLeft && !!canUseFreePush;
  const isCanBeChangedActivationStatus = (
    (isProductAvailable || isProductReserved)
    && data.seller?.blockedBy === UserBlockedBy.NotBlocked
  );
  const isCanBeActivated = (
    isCanBeChangedActivationStatus
    && isProductHidden
    && !isProcessingMultipleAction
  );
  const isCanBeDeactivated = (
    isCanBeChangedActivationStatus
    && !isProductHidden
    && !isProcessingMultipleAction
  );
  const isCanAddToSwap = (
    isSwapEnabled
    && data?.sellingCondition !== SellingConditionEnum.Exchange
    && !!data?.isAvailableExchange
  );
  const isCanRemoveFromSwap = (
    isSwapEnabled
    && data?.sellingCondition === SellingConditionEnum.Exchange
    && !!swapInfoData?.removeFromSwapInfoForProduct.isRemoveAvailable
  );
  const isCanMarkAsSold = !!data?.canMarkAsSold;
  const isCanBeEdited = !!data?.canBeEdited;

  useEffect(() => {
    setIsSwapEnabled(!!cookie.get(SWAP_PARTY_COOKIE));
  }, []);

  if (!complete) {
    return (
      <ProductDetailsSellerActionsSkeleton className={className} />
    );
  }

  if (data) {
    return (
      <>
        <ProductDetailsSellerActions
          className={className}
          productId={data.id}
          isProductHidden={isProductHidden}
          isCanBePromoted={isProductCanBePromoted}
          isCanBeReserved={isProductCanBeReserved}
          isCanBeUnReserved={isProductCanBeUnReserved}
          isCanBeFreePushups={isCanBeFreePushups}
          freePushupAvailable={!!freePushupAvailable}
          isCanBeActivated={isCanBeActivated}
          isCanBeDeactivated={isCanBeDeactivated}
          isPromoActionCanBeAdded={isProductAvailable}
          isPromoActionCanBeEdited={isPromoActionAdded}
          isCanBeAddedToSwap={isCanAddToSwap}
          isCanRemoveFromSwap={isCanRemoveFromSwap}
          isCanMarkAsSold={isCanMarkAsSold}
          isCanBeEdited={isCanBeEdited}
          onReserve={handleReserve}
          onUnReserve={handleUnReserve}
          onFreePushup={handleFreePushup}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onAddPromoAction={handleAddOrEditPromoAction}
          onEditPromoAction={handleAddOrEditPromoAction}
          onAddToSwap={handleAddToSwap}
          onRemoveFromSwap={handleRemoveFromSwapClick}
        />

        <PromoActionFormModalContainer
          isOpen={isPromoActionFormModalOpen}
          productsIds={[product?.id as number]}
          promoType={PromoActionTypeEnum.FreeShipping}
          onCreateOrUpdateSuccess={handleAddOrEditPromoActionSuccess}
          onRemoveSuccess={handleDeletePromoActionSuccess}
          onClose={() => setIsPromoActionFormModalOpen(false)}
        />

        <RemoveProductFromSwapInfoModal
          isOpen={!!swapRemoveModalInfoData}
          data={swapRemoveModalInfoData}
          onRemove={handleRemoveProductFromSwap}
          onClose={() => setSwapRemoveModalInfoData(null)}
        />
      </>
    );
  }

  return null;
};
